/** How NxZoomControl moves: additive `step` (default) or multiplicative `factor`. */
export interface NxZoomStepOptions {
  /** Smallest factor. Default 0.25. */
  min?: number;
  /** Largest factor. Default 4. */
  max?: number;
  /** Additive step (0.1 = 10 percentage points). Default 0.1. */
  step?: number;
  /**
   * Multiplicative step: `+` multiplies by it, `−` divides (1.25 → 100, 125,
   * 156 … %). Wins over `step` when greater than 1 — for maps and floor plans,
   * where every step should feel the same at any zoom.
   */
  factor?: number;
}

export const NX_ZOOM_DEFAULTS = { min: 0.25, max: 4, step: 0.1 } as const;

/** Keeps a factor within min–max and rounds away float residue (0.1 + 0.2). */
export function nxZoomClamp(value: number, options: NxZoomStepOptions = {}): number {
  const min = options.min ?? NX_ZOOM_DEFAULTS.min;
  const max = options.max ?? NX_ZOOM_DEFAULTS.max;
  const v = Number.isFinite(value) ? value : 1;
  return Math.min(max, Math.max(min, Math.round(v * 1000) / 1000));
}

/**
 * The next zoom factor one step in (`direction` 1) or out (-1) — the logic of
 * NxZoomControl's buttons and shortcuts, for a caller's own keys or gestures.
 */
export function nxZoomStep(
  value: number,
  direction: 1 | -1,
  options: NxZoomStepOptions = {}
): number {
  const factor = options.factor;
  if (factor !== undefined && Number.isFinite(factor) && factor > 1) {
    return nxZoomClamp(direction > 0 ? value * factor : value / factor, options);
  }
  return nxZoomClamp(value + direction * (options.step ?? NX_ZOOM_DEFAULTS.step), options);
}
